import { IconSelectClean } from "@components/inputs/select/IconSelect";
import { IGetValueArgs } from "@components/smart/FieldInfo";
import BindingContext from "@odata/BindingContext";
import { EntityTypeName, IPrDeductionEntity, IPrDeductionTemporalEntity } from "@odata/GeneratedEntityTypes";
import {
    ClearedStatusCode,
    PrChildSupportDeductionTypeCode,
    PrDeductionCalculationTypeCode,
    PrDeductionTypeCode
} from "@odata/GeneratedEnums";
import { getEnumSelectItems } from "@odata/GeneratedEnums.utils";
import i18next from "i18next";
import React from "react";

import { ToolbarItemType } from "../../../enums";
import { TableStorage } from "../../../model/TableStorage";
import { ICustomButtonDefArgs, TableActionOrder, TTableToolbarItem } from "../../../views/table/TableToolbar.utils";

export const DEDUCTION_ADD_BUTTON = "deductionAddButton";
export const ADD_EMPLOYEE_ITEM = "addEmployeeItem";
export const INSOLVENCY_DEPOSIT_CLOSING = "insolvencyDepositClosing";

export const isAmongTypes = (types: PrDeductionTypeCode[]) => (args: IGetValueArgs): boolean => {
    return types.includes(args.storage.data.entity.TypeCode);
};

export const isInsolvencyDepositClosed = (args: IGetValueArgs): boolean => {
    const entity = args.storage.data.entity as IPrDeductionEntity;
    return !!entity?.InsolvencyDepositClosingCode;
};

export const hasDepositClosingDocumentBeenLiquidated = (entity: IPrDeductionEntity): boolean => {
    const code = entity?.DepositClosingDocument?.ClearedStatusCode;
    return code === ClearedStatusCode.Cleared || code === ClearedStatusCode.PartiallyCleared;
};

export const isRegularChildSupport = (args: IGetValueArgs): boolean => {
    return args.storage.data.entity.TypeCode === PrDeductionTypeCode.ChildSupport && args.storage.data.entity.ChildSupportType?.Code === PrChildSupportDeductionTypeCode.Regular;
};

export const isDebtTypeOfChildSupport = (args: IGetValueArgs): boolean => {
    return args.storage.data.entity.TypeCode !== PrDeductionTypeCode.ChildSupport || args.storage.data.entity.ChildSupportType?.Code !== PrChildSupportDeductionTypeCode.Regular;
};

export const isDecreeRequiredForChildSupport = (args: IGetValueArgs): boolean => {
    const entity = args.storage.data.entity as IPrDeductionEntity;
    return entity.TypeCode === PrDeductionTypeCode.ChildSupport && (
        entity.ChildSupportType?.Code === PrChildSupportDeductionTypeCode.Assigned ||
        entity.ChildSupportType?.Code === PrChildSupportDeductionTypeCode.Substitute
    );
};

export const DEBT_BALANCE_PATH = BindingContext.localContext("DeductionBalance");
export const PAID_AMOUNT_PATH = BindingContext.localContext("PaidAmount");

type DeductionTypeCodeString = `Deduction${PrDeductionTypeCode}`

function getIconByDeductionTypeCode(typeCode: PrDeductionTypeCode): DeductionTypeCodeString {
    switch (typeCode) {
        case PrDeductionTypeCode.ChildSupport:
            return "DeductionChildSupport";
        case PrDeductionTypeCode.Custom:
            return "DeductionCustom";
        case PrDeductionTypeCode.Distrainment:
            return "DeductionDistrainment";
        case PrDeductionTypeCode.Insolvency:
            return "DeductionInsolvency";
        case PrDeductionTypeCode.InsolvencyAdministratorReward:
            return "DeductionInsolvencyReward";
        case PrDeductionTypeCode.InsolvencyDeposit:
            return 'DeductionInsolvencyDeposit';
        // case PrDeductionTypeCode.Insolvence(Deponace): // todo: not implemented, but exist in zeplin and I got icon from designer
            // return "DeductionDeposit";
    }
}

export function getToolbarAddButtonConfig(storage: TableStorage, ref: React.RefObject<IconSelectClean>, args?: ICustomButtonDefArgs): TTableToolbarItem {
    const items = getEnumSelectItems(EntityTypeName.PrDeductionType).map(item => ({
        ...item,
        iconName: getIconByDeductionTypeCode(item.id as PrDeductionTypeCode),
    }));

    return {
        id: DEDUCTION_ADD_BUTTON,
        itemType: ToolbarItemType.IconSelect,
        iconName: "Add",
        label: i18next.t("Deduction:NewDeduction"),
        order: TableActionOrder.Add,
        itemProps: {
            isTransparent: true,
            selectProps: {
                isActive: storage.data.addingRow || args?.isActive,
                isDisabled: !!storage.tableAction || args?.isDisabled,
                ref
            },
            items
        }
    };
}

export function isPercentageAmountDeduction(args: IGetValueArgs): boolean {
    const { storage, bindingContext } = args;
    const deductionTemporalEntity = storage.getValue(bindingContext.getParent()) as IPrDeductionTemporalEntity;
    return deductionTemporalEntity.CalculationTypeCode !== PrDeductionCalculationTypeCode.Fixed
        && args.storage.data.entity.TypeCode !== PrDeductionTypeCode.ChildSupport && args.storage.data.entity.TypeCode !== PrDeductionTypeCode.InsolvencyAdministratorReward;
}